import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import { EditorSaveEditOrReviewRequestPortfolioSavingModeEnum } from "@eu.mdotm.api/sphere";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogFooter, DialogHeader } from "@mdotm/shadcn/legacy/components";
import type { AsyncState } from "@mdotm/shadcn/legacy/headless";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { Button, FormField, Radio, RadioGroup, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

type ChooseEntitySavingModeDialogProps = {
	onSubmitAsync(payload: { portfolioSavingMode: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum }): Promise<void>;
	portfolioSavingMode: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum;
} & MinimumDialogProps;

type ChooseEntitySavingModeFooterProps = { state: AsyncState; onClose?(): void };

function ChooseEntitySavingModeFooter({ state, onClose }: ChooseEntitySavingModeFooterProps) {
	const { t } = useTranslation();
	return (
		<DialogFooter
			primaryAction={
				<SubmitButton
					palette="primary"
					disabled={state === "loading"}
					data-qualifier="EditPortfolioComposition/SaveDialog/Save"
				>
					{t("BUTTON.SAVE")}
				</SubmitButton>
			}
			neutralAction={
				<Button
					palette="tertiary"
					disabled={state === "loading"}
					onClick={onClose}
					data-qualifier="EditPortfolioComposition/SaveDialog/Cancel"
				>
					{t("BUTTON.CANCEL")}
				</Button>
			}
		/>
	);
}

function renderChooseEntitySavingModeFooter(props: ChooseEntitySavingModeFooterProps) {
	return <ChooseEntitySavingModeFooter {...props} />;
}

function ChooseEntitySavingModeDialog(props: ChooseEntitySavingModeDialogProps) {
	const { control, formState, handleSubmit } = useForm({
		defaultValues: {
			portfolioSavingMode: props.portfolioSavingMode,
		},
		resolver: zodResolver(
			z.object({
				portfolioSavingMode: z.nativeEnum(EditorSaveEditOrReviewRequestPortfolioSavingModeEnum),
			}),
		),
	});

	return (
		<Dialog
			show={props.show}
			onAnimationStateChange={props.onAnimationStateChange}
			size="large"
			onClose={props.onClose}
			onSubmitAsync={() => handleSubmit((x) => props.onSubmitAsync({ portfolioSavingMode: x.portfolioSavingMode }))()}
			header={<DialogHeader>Would you like to save this as a Nested Portfolio?</DialogHeader>}
			footer={renderChooseEntitySavingModeFooter}
		>
			<p className="mb-4">By keeping it as a Nested Portfolio, internal portfolios will automatically update</p>
			<FormField
				label="Composition mode"
				error={(valueByPath(formState.errors, "portfolioSavingMode") as { message?: string })?.message}
			>
				{(fieldProps) => (
					<FormController
						control={control}
						name="portfolioSavingMode"
						defaultValue={EditorSaveEditOrReviewRequestPortfolioSavingModeEnum.NestPortfolios}
						render={({ field: { ref: _ref, ...controllerProps } }) => (
							<RadioGroup {...controllerProps} {...fieldProps} onChange={controllerProps.onChange}>
								<div className="flex flex-row flex-wrap gap-4">
									<Radio
										value={EditorSaveEditOrReviewRequestPortfolioSavingModeEnum.NestPortfolios}
										data-qualifier="EditPortfolioComposition/SaveDialog/CompositionMode(Nested)"
									>
										Keep as Nested
									</Radio>
									<Radio
										value={EditorSaveEditOrReviewRequestPortfolioSavingModeEnum.MixInstruments}
										data-qualifier="EditPortfolioComposition/SaveDialog/CompositionMode(Aggregate)"
									>
										Aggregate the instruments
									</Radio>
								</div>
							</RadioGroup>
						)}
					/>
				)}
			</FormField>
		</Dialog>
	);
}

type SpawnChooseEntitySavingModeDialogProps = Omit<
	ChooseEntitySavingModeDialogProps,
	"show" | "onClose" | "onSubmitAsync"
> &
	Partial<Pick<ChooseEntitySavingModeDialogProps, "onSubmitAsync">>;

/** Resolves with the chosen mode once saved, or `null` when the dialog is dismissed. */
export function spawnChooseEntitySavingModeDialog(
	params: SpawnChooseEntitySavingModeDialogProps,
): SpawnResult<EditorSaveEditOrReviewRequestPortfolioSavingModeEnum | null> {
	const { onSubmitAsync, ...forward } = params;
	return spawn<EditorSaveEditOrReviewRequestPortfolioSavingModeEnum | null>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<ChooseEntitySavingModeDialog
				{...forward}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve(null)}
				onSubmitAsync={async ({ portfolioSavingMode }) => {
					await onSubmitAsync?.({ portfolioSavingMode });
					resolve(portfolioSavingMode);
				}}
			/>
		)),
	);
}
